import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../../../Components/Typography";
import Card from "../../../Components/Card";
import Form from "../../../Components/Form";
import Button from "../../../Components/Button";
import { updateUser, uploadUserImage } from "../../../Utils/apiServices";
import { useAuth } from "../../../Context/authContext";
import Avatar from "../../../Components/Avatar";
import EmailOtpDialog from "../../../Components/EmailOtpDialog";
import { getTimezoneOptionsForCountry, getCountryCodeFromName } from "../../../Utils/constants";
import { stripNumbersFromName } from "../../../Utils/nameValidation";

const PersonalInfoTab = ({ initialFormData, onUpdate }) => {
  const { user, updateUser: updateAuthUser } = useAuth();
  const [photo, setPhoto] = useState(initialFormData?.photo || null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [bio, setBio] = useState(initialFormData?.bio || "");
  const [formValues, setFormValues] = useState({ ...initialFormData, bio: initialFormData?.bio || "" });
  const [isDragging, setIsDragging] = useState(false);
  const [otpDialog, setOtpDialog] = useState({ open: false, email: "", pendingData: null });
  const formRef = useRef(null);

  useEffect(() => {
    if (initialFormData?.bio) {
      setBio(initialFormData.bio);
    }
    // Only set photo if it's a valid URL, otherwise set to null to show initials
    const photoUrl = initialFormData?.photo || user?.profile_picture_url;
    if (photoUrl && 
        photoUrl !== "" && 
        !photoUrl.includes("pravatar.cc") && 
        !photoUrl.includes("default") &&
        (photoUrl.startsWith("http") || photoUrl.startsWith("data:image") || photoUrl.startsWith("/"))) {
      setPhoto(photoUrl);
    } else {
      setPhoto(null);
    }
    setFormValues({ ...initialFormData, bio: initialFormData?.bio || "" });
  }, [initialFormData, user?.profile_picture_url]);

  const getFormFields = () => {
    const isMentor = user?.role === 'mentor';
    const baseFields = [
      {
        name: "firstName",
        label: "Name",
        type: "custom",
        required: true,
        render: (value, formData, handleInputChange, error, errors = {}, touched = {}, handleBlur) => {
          const firstNameError = errors?.firstName && touched?.firstName;
          const lastNameError = errors?.lastName && touched?.lastName;
          const hasError = firstNameError || lastNameError;
          return (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.firstName || ""}
                  onChange={(e) => handleInputChange("firstName", stripNumbersFromName(e.target.value))}
                  onBlur={() => {
                    if (handleBlur) handleBlur("firstName");
                  }}
                  name="firstName"
                  placeholder="First Name"
                  className={`flex-1 rounded-lg border ${
                    firstNameError ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
                />
                <input
                  type="text"
                  value={formData.lastName || ""}
                  onChange={(e) => handleInputChange("lastName", stripNumbersFromName(e.target.value))}
                  onBlur={() => {
                    if (handleBlur) handleBlur("lastName");
                  }}
                  name="lastName"
                  placeholder="Last Name"
                  className={`flex-1 rounded-lg border ${
                    lastNameError ? "border-red-400" : "border-slate-200"
                  } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
                />
              </div>
              {hasError && (
                <p className="text-xs text-red-500">
                  {firstNameError || lastNameError}
                </p>
              )}
            </div>
          );
        },
      },
      {
        name: "lastName",
        label: "",
        type: "hidden",
        required: true,
      },
      {
        name: "username",
        label: "Username",
        type: "text",
        required: false,
        className: "col-span-1",
        disabled: true,
        helperText: "Username cannot be changed",
      },
      {
        name: "email",
        label: "Email address",
        type: "email",
        required: true,
        startIcon: "lucide:mail",
      },
      {
        name: "photo",
        label: "Your photo",
        type: "custom",
        required: true,
        infoIcon: "lucide:info",
        helperText: "This will be displayed on your profile.",
        render: (value, formData, handleInputChange, error) => {
          return (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Your photo
                  <span className="text-red-500">*</span>
                </label>
                <Icon
                  icon="lucide:info"
                  className="h-4 w-4 text-slate-400"
                />
              </div>
              <Typography as="p" className="text-xs text-tertiary-600">
                This will be displayed on your profile.
              </Typography>
              <div className="flex items-center gap-4">
                <Avatar
                  src={photo}
                  firstName={formData.firstName || user?.first_name}
                  lastName={formData.lastName || user?.last_name}
                  email={formData.email || user?.email}
                  alt="Profile photo"
                  size="w-20 h-20"
                  showBorder={true}
                  className="border-2 border-slate-200"
                />
                <label
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isDragging
                      ? "border-purple-500 bg-purple-200"
                      : error
                      ? "border-red-400 bg-red-50"
                      : "border-purple-300 bg-purple-50 hover:bg-purple-100"
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Icon
                      icon="lucide:upload"
                      className="h-8 w-8 text-purple-500 mb-2"
                    />
                    <Typography as="p" className="text-sm text-slate-600 text-center px-4">
                      Click to upload or drag and drop
                    </Typography>
                    <Typography as="p" className="text-xs text-slate-500 mt-1">
                      SVG, PNG, JPG or GIF (max. 1600x1600px)
                    </Typography>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload(file);
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          );
        },
      },
      {
        name: "role",
        label: "Role",
        type: "text",
        disabled: true,
      },
    ];

    if (isMentor) {
      baseFields.push(
        {
          name: "phone",
          label: "Phone",
          type: "tel",
          startIcon: "lucide:phone",
        }
      );
    }

    baseFields.push(
      {
        name: "country",
        label: "Country",
        type: "select",
        options: [
          "Australia",
          "United States",
          "United Kingdom",
          "Canada",
        ],
      },
      {
        name: "state",
        label: "State",
        type: "text",
        placeholder: "State or Province",
      },
      {
        name: "city",
        label: "City",
        type: "text",
        placeholder: "City",
      },
      {
        name: "timezone",
        label: "Timezone",
        type: "custom",
        infoIcon: "lucide:info",
        render: (value, formData, handleInputChange, error) => {
          const countryCode = getCountryCodeFromName(formData.country);
          let timezoneOptions = getTimezoneOptionsForCountry(countryCode || "");
          const currentTz = (formData.timezone || "").trim();
          if (currentTz && !timezoneOptions.includes(currentTz)) {
            timezoneOptions = [currentTz, ...timezoneOptions];
          }
          return (
            <div className="relative">
              <Icon
                icon="lucide:clock"
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
              />
              <select
                value={formData.timezone || ""}
                onChange={(e) => handleInputChange("timezone", e.target.value)}
                disabled={!formData.country}
                className={`w-full rounded-lg border ${
                  error ? "border-red-400" : "border-slate-200"
                } bg-white pl-10 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none disabled:bg-slate-100 disabled:cursor-not-allowed`}
              >
                <option value="">
                  {formData.country ? "Select Timezone" : "Select a country first"}
                </option>
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              <Icon
                icon="lucide:chevron-down"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
              />
            </div>
          );
        },
      },
      {
        name: "bio",
        label: "Bio",
        type: "custom",
        required: true,
        helperText: "Write a short introduction.",
        render: (value, formData, handleInputChange, error) => {
          const maxLength = 275;
          const currentLength = bio.length;
          const remaining = maxLength - currentLength;

          return (
            <div className="flex flex-col gap-2">
              <textarea
                value={bio}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= maxLength) {
                    setBio(newValue);
                    handleInputChange("bio", newValue);
                  }
                }}
                rows={4}
                placeholder="Write a short introduction."
                className={`w-full rounded-lg border ${
                  error ? "border-red-400" : "border-slate-200"
                } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none`}
              />
              <p className="text-xs text-slate-500 text-right">
                {remaining} characters left
              </p>
            </div>
          );
        },
      }
    );

    if (isMentor) {
      baseFields.push(
        {
          name: "section_public_visibility",
          type: "custom",
          component: (
            <div className="flex flex-col gap-1 py-4 border-t border-slate-200 mt-4 text-left">
              <Typography as="h2" className="text-lg font-bold text-primary-900">
                Public Visibility
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600">
                Enable this to showcase your profile to the IgniteNext community.
              </Typography>
            </div>
          ),
        },
        {
          name: "public_visibility",
          label: "",
          type: "custom",
          required: false,
          render: (value, formData, handleInputChange) => {
            const publicOn = formData.public_visibility ?? true;
            return (
              <div className="flex w-full items-center justify-start gap-3 text-left">
                <button
                  type="button"
                  role="switch"
                  aria-checked={publicOn}
                  aria-label="Public profile activation"
                  onClick={() => handleInputChange("public_visibility", !publicOn)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 ${publicOn ? "bg-brand-blue" : "bg-slate-200"}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${publicOn ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <span className="text-sm text-slate-700 text-left">I would like to make my profile public.</span>
              </div>
            );
          },
        }
      );
    }

    return baseFields;
  };

  // Gate the save behind OTP verification when the email address is being changed.
  const handleSave = async (formData) => {
    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    const newEmail = (formData.email || "").trim().toLowerCase();
    const currentEmail = (user.email || "").trim().toLowerCase();

    if (newEmail && newEmail !== currentEmail) {
      setSaveMessage({ type: "", text: "" });
      setOtpDialog({ open: true, email: newEmail, pendingData: formData });
      return;
    }

    performSave(formData);
  };

  const handleEmailVerified = () => {
    const pendingData = otpDialog.pendingData;
    setOtpDialog({ open: false, email: "", pendingData: null });
    if (pendingData) {
      performSave(pendingData);
    }
  };

  const performSave = async (formData) => {
    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      let profilePictureUrl = user?.profile_picture_url || null;

      if (uploadedFile) {
        try {
          const uploadResponse = await uploadUserImage(uploadedFile);
          
          let uploadData = uploadResponse.data;
          
          if (uploadData?.data?.data) {
            uploadData = uploadData.data;
          }
          
          const imageUrl = uploadData?.data?.url || uploadData?.url || uploadData?.data?.data?.url;
          
          if (uploadResponse.success && imageUrl) {
            profilePictureUrl = imageUrl;
          } else {
            setSaveMessage({ 
              type: "error", 
              text: uploadResponse.error || uploadData?.error || "Failed to upload image. Please try again." 
            });
            setIsSaving(false);
            return;
          }
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          setSaveMessage({ 
            type: "error", 
            text: uploadError.error || "Failed to upload image. Please try again." 
          });
          setIsSaving(false);
          return;
        }
      }

      const apiData = {
        first_name: formData.firstName || user.first_name || "",
        last_name: formData.lastName || user.last_name || "",
        email: formData.email || user.email || "",
        country: formData.country || user.country || "",
        state: formData.state || user.state || "",
        city: formData.city || user.city || "",
        timezone: formData.timezone || user.timezone || "",
        bio: formData.bio || bio || user.bio || "",
      };

      if (profilePictureUrl) {
        apiData.profile_picture_url = profilePictureUrl;
      }

      if (user?.role === 'mentor') {
        apiData.phone = formData.phone || user.phone || "";
        apiData.public_visibility = formData.public_visibility ?? user.public_visibility ?? true;
      }

      const response = await updateUser(user.id, apiData);

      if (response.success) {
        const updatedUserData = {
          first_name: apiData.first_name,
          last_name: apiData.last_name,
          email: apiData.email,
          country: apiData.country,
          state: apiData.state,
          city: apiData.city,
          timezone: apiData.timezone,
          bio: apiData.bio,
          ...(profilePictureUrl && { profile_picture_url: profilePictureUrl }),
          ...(user?.role === 'mentor' && {
            phone: apiData.phone,
            public_visibility: apiData.public_visibility,
          }),
        };
        
        updateAuthUser(updatedUserData);

        const profileToSave = {
          ...formData,
          state: apiData.state,
          city: apiData.city,
          timezone: apiData.timezone,
          bio: apiData.bio,
          photo: profilePictureUrl || photo,
          ...(user?.role === 'mentor' && {
            public_visibility: apiData.public_visibility,
          }),
        };
        
        setBio(apiData.bio);
        
        if (onUpdate) {
          onUpdate(profileToSave);
        }
        
        setPhoto(profilePictureUrl || photo);
        setUploadedFile(null);

        setSaveMessage({ type: "success", text: "Profile updated successfully!" });
        
        setTimeout(() => {
          setSaveMessage({ type: "", text: "" });
        }, 3000);
      } else {
        setSaveMessage({ 
          type: "error", 
          text: response.error || "Failed to update profile. Please try again." 
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setSaveMessage({ 
        type: "error", 
        text: error.error || "An error occurred while updating your profile." 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormValues({ ...initialFormData, bio: initialFormData?.bio || "" });
    setBio(initialFormData?.bio || "");
    setSaveMessage({ type: "", text: "" });
  };

  const handleFileUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      setUploadedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900 text-left">
              Personal Info
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600 text-left">
              Update your photo and personal details here.
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSave({ ...formValues, bio })}
              disabled={isSaving}
              loading={isSaving}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
            >
              Save
            </Button>
          </div>
        </div>

        {saveMessage.text && (
          <div
            className={`p-4 rounded-lg border ${
              saveMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                icon={
                  saveMessage.type === "success"
                    ? "lucide:check-circle"
                    : "lucide:alert-circle"
                }
                className={`h-5 w-5 ${
                  saveMessage.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              />
              <Typography as="p" className="text-sm font-medium">
                {saveMessage.text}
              </Typography>
            </div>
          </div>
        )}

        <div ref={formRef}>
          <Form
            fields={getFormFields()}
            initialValues={formValues}
            onChange={(data) => setFormValues({ ...data })}
            showActions={false}
            layout="horizontal-label"
            loading={isSaving}
          />
        </div>
      </div>

      <EmailOtpDialog
        isOpen={otpDialog.open}
        email={otpDialog.email}
        onClose={() => setOtpDialog({ open: false, email: "", pendingData: null })}
        onVerified={handleEmailVerified}
      />
    </Card>
  );
};

export default PersonalInfoTab;

